import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { fmt, fmtPct } from '../format';
import useTableControls from '../hooks/useTableControls';

export default function CompletedJobsPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getWipCompleted()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const { search, setSearch, sorted, toggleSort, sortIcon } = useTableControls(data, { key: 'revenue_earned', dir: 'desc' });

  const totals = useMemo(() => {
    return sorted.reduce((acc, r) => ({
      revenue: acc.revenue + (r.revenue_earned || 0),
      costs: acc.costs + (r.cost_of_revenue || 0),
      gp: acc.gp + (r.gross_profit || 0),
    }), { revenue: 0, costs: 0, gp: 0 });
  }, [sorted]);

  if (loading) return <div className="page-loading">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Completed Jobs</h1>
          <p className="page-subtitle">Projects in Warranty &mdash; {sorted.length} projects</p>
        </div>
        <div className="page-actions">
          <input type="text" placeholder="Search projects..." value={search}
            onChange={e => setSearch(e.target.value)} className="table-search" />
          <button className="btn btn-secondary" onClick={() => window.print()}>Print Report</button>
        </div>
      </div>

      <div className="report-header-bar">
        <div className="report-title">CM Corp &mdash; Completed Projects</div>
        <div className="report-date">As of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      <div className="table-container wip-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-id sortable" onClick={() => toggleSort('project_number')}>No.{sortIcon('project_number')}</th>
              <th className="col-project sortable" onClick={() => toggleSort('project_name')}>Project{sortIcon('project_name')}</th>
              <th className="col-text sortable" onClick={() => toggleSort('project_manager')}>PM{sortIcon('project_manager')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('revenue_earned')}>Revenue Earned{sortIcon('revenue_earned')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('cost_of_revenue')}>Cost of Revenue{sortIcon('cost_of_revenue')}</th>
              <th className="col-money sortable" onClick={() => toggleSort('gross_profit')}>Gross Profit{sortIcon('gross_profit')}</th>
              <th className="col-pct sortable" onClick={() => toggleSort('gross_profit_pct')}>GP %{sortIcon('gross_profit_pct')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.project_id}>
                <td className="col-id">{r.project_number}</td>
                <td className="col-project">
                  {r.project_name}
                  <a href={`https://app.procore.com/9528/${r.project_id}/project/home`}
                     target="_blank" rel="noopener noreferrer" className="procore-link"
                     title="Open in Procore">&#8599;</a>
                </td>
                <td className="col-text">{r.project_manager || '—'}</td>
                <td className="col-money">{fmt(r.revenue_earned)}</td>
                <td className="col-money">{fmt(r.cost_of_revenue)}</td>
                <td className={`col-money ${r.gross_profit < 0 ? 'negative' : ''}`}>{fmt(r.gross_profit)}</td>
                <td className="col-pct">{fmtPct(r.gross_profit_pct)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="grand-total-row">
              <td></td>
              <td className="col-project">Grand Total</td>
              <td></td>
              <td className="col-money">{fmt(totals.revenue)}</td>
              <td className="col-money">{fmt(totals.costs)}</td>
              <td className={`col-money ${totals.gp < 0 ? 'negative' : ''}`}>{fmt(totals.gp)}</td>
              <td className="col-pct">{fmtPct(totals.revenue > 0 ? totals.gp / totals.revenue : 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
